import { Button, Icon, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { subscriptions as subscriptionsService } from "../../api/subscriptions.service";
import { SubscriptionModal } from "./SubscriptionModal";
import { PageHeader } from "../../components/PageHeader";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import PermissionGate from "../../components/Permissions/PermissionGate";
import PermissionBanner from "../../components/Permissions/PermissionBanner";
import { ROUTES } from "../../routes";
import styles from "./Subscriptions.module.css";

function SubscriptionsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    async function checkAuth() {
      try {
        await subscriptionsService.getAll({ limit: 1 });
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to check auth");
        const status = (error as Error & { status?: number }).status;
        if (status === 401 || status === 403) {
          setAuthError(error);
        }
      }
    }
    checkAuth();
  }, []);

  function handleOpenCreateModal() {
    setIsCreateModalOpen(true);
  }

  function handleCloseCreateModal() {
    setIsCreateModalOpen(false);
  }

  function handleCreateSuccess() {
    setIsCreateModalOpen(false);
    setToast({
      message: "Subscription created successfully",
      type: "success",
    });
    setRefetchTrigger(prev => prev + 1);
  }

  function handleCloseToast() {
    setToast(null);
  }

  function handleMutationSuccess() {
    setRefetchTrigger(prev => prev + 1);
  }

  function handleBackClick() {
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
    } else {
      navigate(ROUTES.fulfilment.subscriptions);
    }
  }

  const isDetailPage = location.pathname !== ROUTES.fulfilment.subscriptions;

  if (authError) {
    return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader
          title="Subscriptions"
          subtitle="Manage subscription lifecycle and fulfilment operations"
        >
          <div className={styles.headerActions}>
            {isDetailPage && (
              <Button variant="outlined" color="default" onClick={handleBackClick}>
                <Icon icon="arrow-left" size="small" />
                Back
              </Button>
            )}
            <PermissionGate permission="fulfilment.create_subscription">
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenCreateModal}
                startIcon={<Icon icon="plus" size="small" />}
                className={styles.createButton}
                aria-label="Create Subscription"
              >
                Create
              </Button>
            </PermissionGate>
          </div>
        </PageHeader>
        <PermissionBanner scope="fulfilment" resource="subscriptions" />
      </div>

      <Outlet
        context={{
          toast,
          setToast,
          refetchTrigger,
          onMutationSuccess: handleMutationSuccess,
        }}
      />

      <SubscriptionModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        subscription={null}
        mode="create"
        onSuccess={handleCreateSuccess}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
          duration={5000}
        />
      )}
    </div>
  );
}

export default SubscriptionsLayout;
